import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { TimePickerInput } from "../../../common/TimePickerInput";
import styles from "./EmployeeShiftsModal.module.css";

// Helper function to calculate total time between start and end times
// Handles overnight shifts (when end time is before start time)
const calculateTotalTime = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return null;
  }

  const getMinutes = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  let startMinutes = getMinutes(startTime);
  let endMinutes = getMinutes(endTime);

  // If end time is less than start time, add 24 hours (overnight shift)
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }

  return Math.round(((endMinutes - startMinutes) / 60) * 100) / 100;
};

// Individual shift row within the modal
const ModalShiftRow = ({
  shiftId,
  shift,
  onStartTimeChange,
  onEndTimeChange,
  onRemove,
  disabled,
  errors,
}) => {
  const startTimeErrorClass = errors?.shiftStartTime ? styles.errorInput : "";
  const endTimeErrorClass = errors?.shiftEndTime ? styles.errorInput : "";

  return (
    <tr>
      <td>
        <TimePickerInput
          value={shift.startTime || ""}
          onChange={(time) => onStartTimeChange(shiftId, time)}
          disabled={disabled}
          className={`${styles.timeInput} ${startTimeErrorClass}`}
        />
      </td>
      <td>
        <TimePickerInput
          value={shift.endTime || ""}
          onChange={(time) => onEndTimeChange(shiftId, time)}
          disabled={disabled}
          className={`${styles.timeInput} ${endTimeErrorClass}`}
        />
      </td>
      <td>
        <input
          type="text"
          value={shift.totalTime != null ? shift.totalTime : ""}
          readOnly
          className={styles.totalTimeInput}
        />
      </td>
      <td>
        <button
          onClick={() => onRemove(shiftId)}
          className={styles.removeShiftButton}
          disabled={disabled}
        >
          Remove
        </button>
      </td>
    </tr>
  );
};

// Main modal component for managing employee shifts
export const EmployeeShiftsModal = ({
  isOpen,
  onClose,
  onSave,
  employee,
  delMode,
  errors,
}) => {
  const modalRef = useRef(null);
  const isSavingRef = useRef(false);

  // Initialize tempState from employee's current shifts
  const [tempState, setTempState] = useState(() => {
    const shifts = {};
    const originalShifts = {};
    const existingShiftIds = [];

    employee.shifts.forEach((shift) => {
      existingShiftIds.push(shift.id);
      shifts[shift.id] = {
        startTime: shift.startTime || null,
        endTime: shift.endTime || null,
        totalTime: shift.totalTime || null,
      };
      originalShifts[shift.id] = {
        startTime: shift.startTime || null,
        endTime: shift.endTime || null,
        totalTime: shift.totalTime || null,
      };
    });

    return { shifts, existingShiftIds, originalShifts };
  });

  // Reset tempState when employee changes or modal opens
  useEffect(() => {
    if (isOpen) {
      const shifts = {};
      const originalShifts = {};
      const existingShiftIds = [];

      employee.shifts.forEach((shift) => {
        existingShiftIds.push(shift.id);
        shifts[shift.id] = {
          startTime: shift.startTime || null,
          endTime: shift.endTime || null,
          totalTime: shift.totalTime || null,
        };
        originalShifts[shift.id] = {
          startTime: shift.startTime || null,
          endTime: shift.endTime || null,
          totalTime: shift.totalTime || null,
        };
      });

      setTempState({ shifts, existingShiftIds, originalShifts });
      isSavingRef.current = false;
    }
  }, [isOpen, employee]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  // Note: We need to check if the click is on a time picker dropdown (portaled to body)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the modal
      if (modalRef.current && modalRef.current.contains(event.target)) {
        return;
      }

      // Check if click is on a time picker element (dropdown is portaled to body)
      // TimePickerInput uses CSS modules with classes like 'timeDropdown', 'timePickerContainer'
      const isTimePickerElement = event.target.closest(
        "[class*='timeDropdown'], [class*='timeOption'], [class*='timePickerContainer'], " +
        "[class*='TimePicker'], [class*='time-picker']"
      );

      if (isTimePickerElement) {
        return;
      }

      onClose();
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle start time change - update local state only
  const handleStartTimeChange = (shiftId, time) => {
    setTempState((prev) => {
      const currentShift = prev.shifts[shiftId];
      const newTotalTime = calculateTotalTime(time, currentShift.endTime);

      return {
        ...prev,
        shifts: {
          ...prev.shifts,
          [shiftId]: {
            ...currentShift,
            startTime: time,
            totalTime: newTotalTime,
          },
        },
      };
    });
  };

  // Handle end time change - update local state only
  const handleEndTimeChange = (shiftId, time) => {
    setTempState((prev) => {
      const currentShift = prev.shifts[shiftId];
      const newTotalTime = calculateTotalTime(currentShift.startTime, time);

      return {
        ...prev,
        shifts: {
          ...prev.shifts,
          [shiftId]: {
            ...currentShift,
            endTime: time,
            totalTime: newTotalTime,
          },
        },
      };
    });
  };

  // Handle adding a new shift - local state only
  const handleAddShift = () => {
    const newShiftId = uuidv4();
    setTempState((prev) => ({
      ...prev,
      shifts: {
        ...prev.shifts,
        [newShiftId]: {
          startTime: null,
          endTime: null,
          totalTime: null,
        },
      },
    }));
  };

  // Handle removing a shift - local state only
  const handleRemoveShift = (shiftId) => {
    setTempState((prev) => {
      const { [shiftId]: removed, ...remainingShifts } = prev.shifts;
      return {
        ...prev,
        shifts: remainingShifts,
      };
    });
  };

  // Handle save - pass tempState to parent for patch emission
  const handleSave = () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    onSave(tempState);
  };

  // Handle cancel - close without saving
  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  const shiftEntries = Object.entries(tempState.shifts);
  const hasMaxShifts = shiftEntries.length >= 3;
  const isViewMode = delMode === "view";

  // Get employee display name
  const employeeDisplayName = employee.employeeName || "Employee";

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3>Manage Shifts - {employeeDisplayName}</h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          {shiftEntries.length === 0 ? (
            <div className={styles.noShiftsMessage}>
              No shifts added. Click "Add Shift" to add one.
            </div>
          ) : (
            <table className={styles.shiftsTable}>
              <thead>
                <tr>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Total Hours</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {shiftEntries.map(([shiftId, shift]) => (
                  <ModalShiftRow
                    key={shiftId}
                    shiftId={shiftId}
                    shift={shift}
                    onStartTimeChange={handleStartTimeChange}
                    onEndTimeChange={handleEndTimeChange}
                    onRemove={handleRemoveShift}
                    disabled={isViewMode}
                    errors={errors?.shifts?.find((e) => e.shiftId === shiftId)}
                  />
                ))}
              </tbody>
            </table>
          )}

          {!hasMaxShifts && !isViewMode && (
            <button onClick={handleAddShift} className={styles.addShiftButton}>
              Add Shift
            </button>
          )}

          {hasMaxShifts && !isViewMode && (
            <div className={styles.maxShiftsMessage}>
              Maximum of 3 shifts reached
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={handleCancel} className={styles.cancelButton}>
            {isViewMode ? "Close" : "Cancel"}
          </button>
          {!isViewMode && (
            <button onClick={handleSave} className={styles.saveButton}>
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};